import React, { useState } from 'react';
import './LoadFilesModal.scss';

/**
 * Modal for viewing load attached PDF files only
 * Groups files by category: Vehicle, Freight, General
 */
const LoadFilesModal = ({ isOpen, onClose, load }) => {
  const [activeTab, setActiveTab] = useState('all');

  if (!isOpen || !load) return null;

  // Gather all PDF files from load data (only PDFs, no images)
  const loadData = load.load || load;
  
  const fileCategories = {
    vehicle: {
      label: 'Vehicle',
      icon: '🚗',
      pdfs: loadData.vehicle?.pdfs || []
    },
    freight: {
      label: 'Freight',
      icon: '📦',
      pdfs: loadData.freight?.pdfs || []
    },
    general: {
      label: 'General',
      icon: '📁',
      pdfs: [
        ...(loadData.pdfs || []),
        ...(loadData.documents || []),
        ...(loadData.bolPdfPath ? [loadData.bolPdfPath] : []),
        ...(loadData.rateConfirmationPdfPath ? [loadData.rateConfirmationPdfPath] : [])
      ]
    }
  };

  // Count files per category (only PDFs)
  const getCategoryCount = (category) => {
    return category.pdfs.length;
  };

  // Get all PDF files for "All" tab
  const getAllFiles = () => {
    const allFiles = [];
    Object.entries(fileCategories).forEach(([key, category]) => {
      category.pdfs.forEach(url => {
        allFiles.push({ url, type: 'pdf', category: category.label });
      });
    });
    return allFiles;
  };

  const allFiles = getAllFiles();
  const totalCount = allFiles.length;

  const handleFileClick = (fileUrl) => {
    window.open(fileUrl, '_blank');
  };

  const getFileName = (url) => {
    if (!url) return 'Unknown file';
    try {
      // Remove query params and get filename
      const cleanUrl = url.split('?')[0];
      const parts = cleanUrl.split('/');
      return decodeURIComponent(parts[parts.length - 1]) || 'File';
    } catch {
      return 'File';
    }
  };

  const getFileIcon = () => {
    return '📕'; // Always PDF icon since we only show PDFs
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderFiles = () => {
    let filesToRender = [];

    if (activeTab === 'all') {
      filesToRender = allFiles;
    } else {
      const category = fileCategories[activeTab];
      if (category) {
        category.pdfs.forEach(url => {
          filesToRender.push({ url, type: 'pdf', category: category.label });
        });
      }
    }

    if (filesToRender.length === 0) {
      return (
        <div className="load-files-modal__empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14,2 14,8 20,8" />
          </svg>
          <p>No PDF files in this category</p>
        </div>
      );
    }

    return (
      <div className="load-files-modal__list">
        {filesToRender.map((file, index) => (
          <div 
            key={index} 
            className="load-files-modal__file"
            onClick={() => handleFileClick(file.url)}
          >
            <span className="load-files-modal__file-icon">
              {getFileIcon()}
            </span>
            <div className="load-files-modal__file-info">
              <span className="load-files-modal__file-name">
                {getFileName(file.url)}
              </span>
              {activeTab === 'all' && (
                <span className="load-files-modal__file-category">
                  {file.category}
                </span>
              )}
            </div>
            <span className="load-files-modal__file-action">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="load-files-modal__backdrop" onClick={handleBackdropClick}>
      <div className="load-files-modal">
        {/* Header */}
        <div className="load-files-modal__header">
          <h2 className="load-files-modal__title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14,2 14,8 20,8" />
            </svg>
            Load PDF Files
            <span className="load-files-modal__order-id">
              #{loadData.orderId || 'N/A'}
            </span>
          </h2>
          <button className="load-files-modal__close-btn" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="load-files-modal__tabs">
          <button 
            className={`load-files-modal__tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All <span className="count">{totalCount}</span>
          </button>
          {Object.entries(fileCategories).map(([key, category]) => {
            const count = getCategoryCount(category);
            if (count === 0) return null;
            return (
              <button 
                key={key}
                className={`load-files-modal__tab ${activeTab === key ? 'active' : ''}`}
                onClick={() => setActiveTab(key)}
              >
                {category.icon} {category.label} <span className="count">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="load-files-modal__content">
          {renderFiles()}
        </div>

        {/* Footer */}
        <div className="load-files-modal__footer">
          <span className="load-files-modal__count">
            {totalCount} file{totalCount !== 1 ? 's' : ''} total
          </span>
          <button className="load-files-modal__btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoadFilesModal;


